import { Box } from "@dxp-feed/ui/Box";
import { Text } from "@dxp-feed/ui/Text";
import OffersCardHeader from "@/templates/offers/_components/OffersCardHeader";
import { formatDay } from "@/utils/dateFormat";
import { Button } from "@dxp-feed/ui/Button";
import {
  useAppDispatch,
  useAppSelector,
  useEffect,
  useRef,
  useState,
} from "@/utils/hooks/reactUseCustomHooks";
import { SyntheticEvent } from "react";
import { addOffersToList, deleteOffersFromList } from "@/services/mulesoft";
import { RootState } from "@/utils/redux/store";
import { dataLayerOffers } from "@/helpers/dataLayer";
import ImageWrapper from "@/components/ImageWrapper";
import { removeHTMLTags, stripHtmlTags } from "@/helpers/useCommonFunctions";
import {
  offersRemovedFromStorage,
  offersToBeAddedFromStorage,
  updateFavouriteOffers,
} from "@/utils/redux/favOffers";
import ProductLoginModal from "@/components/ProductLoginModal";
import { useGigya } from "@dxp-feed/gigya";
import { useGigyaAccountInfo } from "@/helpers/gigyaCentralSsoHook";

export default function FeaturedOffersCard({
  featuredCardData,
  offerAddedInList,
  openDialog,
  isHash,
  onVisibilityChange,
}: any) {
  const { loggedIn: userLoggedIn, uid: Uid } = useAppSelector(
    (state: RootState) => state.gigyaslice
  );
  const { gigya } = useGigya();

  const [flipped, setFlipped] = useState(false); // State to manage flip
  const [offersQuantity, setOfferQuantity] = useState(
    offerAddedInList === 1 ? true : false
  );
  const offers =
    typeof window != "undefined"
      ? sessionStorage.getItem("offers") || "[]"
      : "[]";
  const [isActive, setIsActive] = useState(false); // State to control Terms & Conditions visibility
  const [isLoaded, setIsLoaded] = useState(false); // State to track if component is loaded
  const flipButtonRef = useRef<HTMLButtonElement>(null); // Ref for the flip button
  const closeButtonRef = useRef<HTMLButtonElement>(null); // Ref for the close button
  const termsButtonRef = useRef<HTMLButtonElement>(null); // Ref for Terms & Conditions button
  const cardRef = useRef<HTMLDivElement>(null); // Ref for the entire card component
  const { favOffers } = useAppSelector((state) => state.favoirateOffersSlice);
  const dispatch = useAppDispatch();

  const [loginModalOpen, setloginModalOpen] = useState(false);
  const [showQuantityText, setShowQuantityText] = useState(false);
  const [Quantity, setQuantity] = useState(offerAddedInList);
  const [token, setToken] = useState("");
  const managementListData: any = useAppSelector(
    (state) => state?.manageListSlice
  );
  const favouriteList: any = useAppSelector(
    (state) => state?.favouriteProductSlice
  );
  const [incrementDisable, setincrementDisable] = useState(false);
  const handleLoginModal = () => {
    setloginModalOpen((prev) => !prev);
    setShowQuantityText(false);
  };
  const shoppingListData: any = useAppSelector(
    (state) => state?.cartProductSlice
  );
  // Set component as loaded after mount
  useEffect(() => {
    setQuantity(offerAddedInList);
    setOfferQuantity(offerAddedInList === 1);
    if (isHash && !userLoggedIn) {
      const existingOffers: any[] = JSON.parse(offers);
      const filter = existingOffers.filter(
        (itm) => itm.offerId == featuredCardData?.offerId
      );
      if (filter?.length > 0) {
        setQuantity(1);
        setOfferQuantity(true);
      } else {
        setQuantity(0);
        setOfferQuantity(false);
      }
    }
  }, [offerAddedInList, offers]);

  // Modified toggleFlip to handle focus management properly
  const toggleFlip = (e: SyntheticEvent) => {
    e.stopPropagation(); // Prevent event bubbling

    if (!flipped) {
      // When opening flip panel, set focus to the back panel after flip animation
      setFlipped(true);
      // Wait for animation to complete before focusing
      setTimeout(() => {
        closeButtonRef.current?.focus();
      }, 500);
    } else {
      // When closing flip panel, we'll focus back to the original button
      setFlipped(false);
      // Wait for animation to complete before focusing
      setTimeout(() => {
        flipButtonRef.current?.focus();
      }, 500);
    }
  };

  const toggleActiveState = (e?: SyntheticEvent) => {
    if (e) {
      e.stopPropagation(); // Prevent event bubbling
    }
    setIsActive((prev) => !prev);
  };

  const closeActiveState = (e?: SyntheticEvent) => {
    if (e) {
      e.stopPropagation(); // Prevent event bubbling
    }
    setIsActive(false);
    // Return focus to the terms button
    setTimeout(() => {
      termsButtonRef.current?.focus();
    }, 300);
  };

  // Handle Terms button keyboard interaction
  const handleTermsButtonKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault(); // Prevent default Enter key behavior
      toggleActiveState(); // Toggle the slide-in Terms & Conditions

      // Wait for animation to complete before focusing
      setTimeout(() => {
        const termsPanel = cardRef.current?.querySelector(".Terms-Slide");
        if (termsPanel instanceof HTMLElement) {
          termsPanel.focus();
        }
      }, 500);
    }
  };

  // Handle Escape key for accessibility
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      if (isActive) {
        closeActiveState();
      } else if (flipped) {
        toggleFlip(e);
      }
    }
  };

  const [imageSrc, setImageSrc] = useState<string | null>(null);

  const handleImageError = () => {
    setImageSrc("/offer_not_found.svg"); // Set fallback image on error
  };
  const formattedStartDate = formatDay(featuredCardData.startDate);
  const formattedEndDate = formatDay(featuredCardData.endDate);

  const addOffersToStorage = () => {
    const storedOffers = sessionStorage.getItem("offers") || "[]";
    const existingOffers: any[] = JSON.parse(storedOffers);

    const isDuplicate = existingOffers.find(
      (offer) => offer.offerId === featuredCardData.offerId
    );

    if (!isDuplicate) {
      existingOffers.push(featuredCardData);
      sessionStorage.setItem("offers", JSON.stringify(existingOffers));
      dispatch(offersToBeAddedFromStorage(existingOffers));
    }
  };

  const deleteOffersFromStorage = () => {
    const storedOffers = sessionStorage.getItem("offers") || "[]";
    const remainingOffers = JSON.parse(storedOffers).filter(
      (offer: any) => offer.offerId !== featuredCardData.offerId
    );
    sessionStorage.setItem("offers", JSON.stringify(remainingOffers));
    dispatch(offersRemovedFromStorage(remainingOffers));
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        // Notify the parent component about the visibility change
        const filteredProduct = (({ offerId, description }) => ({
          item_id: offerId,
          item_name: description,
          affiliation: "Personalized offers",
        }))(featuredCardData);

        if (onVisibilityChange) {
          onVisibilityChange(filteredProduct, entry.isIntersecting);
        }
      },
      {
        root: null,
        rootMargin: "0px",
        threshold: 0.5,
      }
    );
    if (cardRef.current) {
      observer.observe(cardRef.current);
    }
    return () => {
      if (cardRef.current) {
        observer.unobserve(cardRef.current);
      }
    };
  }, []);

  const gigyaToken = useGigyaAccountInfo();

  useEffect(() => {
    const fetchToken = async () => {
      const token = await gigyaToken();
      setToken(token.idToken);
    };

    fetchToken();
  }, [offerAddedInList]);
  return (
    <>
      <Box
        as="div"
        ref={cardRef}
        className={`relative perspective-1000 flex items-stretch w-full h-full ${isLoaded ? "is-loaded" : "is-loading"}`}
        onKeyDown={handleKeyDown}
      >
        <Box
          as="div"
          className={`w-full h-full bg-brandWhite rounded-[12px] transform-style-preserve-3d transition-all duration-500 ${
            flipped ? "rotate-y-180" : ""
          }`}
        >
          {/* Front Side */}
          <Box
            as="div"
            className="frontdata relative h-full rounded-[13px] bg-brandWhite text-brandBlack flex flex-col justify-start backface-hidden border-purple300 overflow-hidden shadow-GlowOfferShadow border-[1px]"
          >
            <OffersCardHeader headerOffers={featuredCardData} />
            <Box
              as="div"
              className="relative items-center justify-center px-4 py-[22px] pb-[36px] min-h-[160px]"
            >
              <Text as="p" className="absolute text-sm text-grey700 top-[2px]">
                {Number(featuredCardData?.daysLeft) === 1 ? (
                  "Expires Today"
                ) : (
                  <>
                    <strong>{featuredCardData?.daysLeft}</strong> days left
                  </>
                )}
              </Text>
              <ImageWrapper
                src={featuredCardData?.imageName}
                width={188}
                height={120}
                className="mx-auto mt-[4px] object-contain h-[95px]"
                alt={featuredCardData.alt ?? ""}
                onError={handleImageError}
              />
              <Text
                as="p"
                className="text-sm absolute mb-[5px] left-0 bottom-[6px] gap-[4px] flex items-center w-full px-[10px] text-left leading-3 justify-between"
              >
                <Text as="span" className="flex flex-row items-center gap-1">
                  <Text
                    as="span"
                    className="icon-check text-white bg-brandPrimary w-[16px] h-[16px] min-w-[16px] min-h-[16px] rounded-full flex items-center justify-center pr-[2px] text-[8px]"
                  ></Text>
                  Loaded on Card
                </Text>
              </Text>
              <Box
                as="div"
                className="absolute right-[10px] top-[15px] h-full items-end justify-between flex flex-col z-0"
              >
                <Box as="div" className="top-0">
                  {removeHTMLTags(featuredCardData.description).length > 90 && (
                    <Button
                      onClick={toggleFlip}
                      variant="icon"
                      className="!p-0 z-0"
                      tabIndex={0}
                      ref={flipButtonRef}
                      aria-label="flip to view offer information"
                      aria-expanded={flipped}
                    >
                      <span className="icon-sponsored w-[40px] h-[40px] text-[22px] flex items-center justify-center text-brandSecondary border-[1px] p-[4px] border-grey350 !rounded-[50%]"></span>
                    </Button>
                  )}
                </Box>
                <Box as="div" className="absolute bottom-[15px] z-0">
                  {featuredCardData?.tcsDescription &&
                    !(
                      featuredCardData?.offerType == 19 ||
                      featuredCardData?.offerType == 20 ||
                      featuredCardData?.offerType == 21
                    ) && (
                      <Text
                        as="span"
                        id="manage_list"
                        tabIndex={0}
                        onClick={() => {
                          const maxQuantity = 1;
                          const quantity = Math.min(Quantity + 1, maxQuantity);
                          if (Quantity < maxQuantity) {
                            setQuantity(quantity);
                            if (!userLoggedIn && isHash) {
                              if (shoppingListData.listName === "") {
                                handleLoginModal();
                                setShowQuantityText(true);
                              }
                              if (quantity > 0) {
                                setOfferQuantity(true);
                                addOffersToStorage();
                              } else {
                                deleteOffersFromStorage();
                                setOfferQuantity(false);
                              }
                            } else {
                              if (
                                managementListData?.itemsList?.length === 0 ||
                                (managementListData?.itemsList?.length === 1 &&
                                  favouriteList?.listName === "Favourites")
                              ) {
                                handleLoginModal();
                                setShowQuantityText(true);
                                return;
                              }
                              if (quantity > 0) {
                                openDialog && openDialog();
                                setOfferQuantity(true);
                                const newOffer = {
                                  offer_id: featuredCardData.offerId,
                                  banner: featuredCardData.bannerInd,
                                  offer_type: featuredCardData.offerType,
                                  offer_description:
                                    JSON.stringify(featuredCardData),
                                };
                                const oldOffers =
                                  favOffers?.favouriteOffers || [];
                                const updatedOffers = [...oldOffers, newOffer];
                                dispatch(updateFavouriteOffers(updatedOffers));
                                addOffersToList(newOffer, token);
                                dataLayerOffers({
                                  event: "add_offer_to_list",
                                  offer_name: stripHtmlTags(
                                    featuredCardData.description
                                  ),
                                  list_name: "personalized_offer",
                                  offer_list_name: "favourite_offers",
                                  page_title: document.title,
                                });
                                return;
                              }
                            }
                          } else {
                            setQuantity(0);
                            setOfferQuantity(false);

                            const filterOffers =
                              favOffers?.favouriteOffers?.filter(
                                (offer) =>
                                  offer?.offer_id !== featuredCardData.offerId
                              ) || [];

                            const updatedOffers = [...filterOffers];
                            dispatch(updateFavouriteOffers(updatedOffers));
                            deleteOffersFromList({
                              offer_id: featuredCardData.offerId,
                              token,
                            });

                            dataLayerOffers({
                              event: "remove_offer_from_list",
                              offer_name: stripHtmlTags(
                                featuredCardData.description
                              ),
                              list_name: "personalized_offer",
                              offer_list_name: "favourite_offers",
                              page_title: document.title,
                            });

                            if (!userLoggedIn && isHash) {
                              deleteOffersFromStorage();
                            }
                          }
                        }}
                        className={`${
                          Quantity > 0 ? "icon-card-filled" : "icon-card"
                        } max-w-full w-auto mobScene text-brandSecondary text-[24px] ml-[6px] cursor-pointer`}
                      ></Text>
                    )}
                </Box>
              </Box>
              {(featuredCardData?.offerType == 19 ||
                featuredCardData?.offerType == 20 ||
                featuredCardData?.offerType == 21) && (
                <Text
                  as="p"
                  className="text-sm absolute right-0 bottom-[6px] gap-[4px] flex items-center w-full px-[10px] text-left leading-3 justify-end"
                >
                  <Text
                    as="span"
                    className="icon-card-filled max-w-full w-auto mobScene text-brandSecondary text-[24px] ml-[6px] cursor-not-allowed"
                  ></Text>
                </Text>
              )}
            </Box>
            <Box
              as="div"
              className="bg-lightpurple300 h-full pt-4 px-4 min-h-[135px]"
            >
              <Box as="div" className="flex flex-col h-full justify-between">
                <Box as="div" className="text-left pb-4">
                  {featuredCardData?.offerLimits?.customer?.limit > 1 && (
                    <Box
                      as="div"
                      className="pt-2 pb-1 flex flex-col items-center justify-center border-grey300 mt-3 border-t-[1px] mb-2"
                    >
                      <Box
                        as="div"
                        className="flex gap-[4px] items-center justify-center"
                      >
                        {Array.from(
                          {
                            length:
                              featuredCardData?.offerLimits?.customer?.limit,
                          },
                          (_, index) => (
                            <Text
                              key={index}
                              as="span"
                              className={`rounded-[50%] inline-flex pt-[3px] pl-[1.5px] text-[8px] w-[14px] h-[14px] ${
                                index <
                                featuredCardData?.offerLimits?.customer
                                  ?.progress
                                  ? "bg-black text-white icon-check"
                                  : "bg-grey300 text-brandBlack"
                              }`}
                            ></Text>
                          )
                        )}
                      </Box>
                      <Text as="span" className="text-grey700 text-xs mt-2">
                        {`${featuredCardData?.offerLimits?.customer?.limit - featuredCardData?.offerLimits?.customer?.progress} ${
                          (featuredCardData?.offerLimits?.customer?.limit - featuredCardData?.offerLimits?.customer?.progress === 1)
                            ? "use"
                            : "uses"
                        } left`}
                      </Text>
                    </Box>
                  )}
                  <Text
                    as="p"
                    className="text-sm font-normal text-brandBlack pt-[4px] pb-[0px]"
                    dangerouslySetInnerHTML={{
                      __html:
                        removeHTMLTags(featuredCardData?.description).length >
                        90
                          ? featuredCardData.description.slice(0, 90) + "..."
                          : featuredCardData.description || "",
                    }}
                  ></Text>
                </Box>
                <Box
                  as="div"
                  className="termsdata w-full text-center border-t-[1px] border-grey350"
                >
                  <Button
                    variant="icon"
                    ref={termsButtonRef}
                    tabIndex={0}
                    className="text-brandSecondary z-0 font-bold text-sm underline !px-0 !pt-[4px] mt-[8px] mb-[10px] !pb-[4px] w-full hover:no-underline"
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent flip when clicking the button
                      toggleActiveState(e); // Show Terms & Conditions
                    }}
                    aria-label="Terms & Conditions"
                    onKeyDown={handleTermsButtonKeyPress}
                    aria-expanded={isActive}
                    aria-controls="terms-panel"
                  >
                    Terms & Conditions
                  </Button>
                </Box>
              </Box>
            </Box>
            {/* Slide-in Terms & Conditions */}
            <Box
              as="div"
              id="terms-panel"
              className={`tab Terms-Slide rounded-[12px] absolute top-0 left-0 right-0 bg-brandWhite p-0 overflow-auto h-full transition-transform duration-500 ease-in-out ${
                isActive ? "translate-y-0" : "translate-y-full"
              }`}
              tabIndex={isActive ? 0 : -1}
              style={{ bottom: 0 }}
              aria-hidden={!isActive}
            >
              <Box
                as="div"
                className="offersCardHoverHeader flex items-center justify-center flex-col pt-[16px] px-4"
              >
                <Button
                  className="rotate-180 closer !p-0"
                  variant="icon"
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent flip when clicking closer button
                    closeActiveState(e); // Hide Terms & Conditions
                  }}
                  tabIndex={isActive ? 0 : -1}
                  aria-label="Close terms and conditions"
                >
                  <Text
                    as="span"
                    className="icon-arrow w-[14px] mr-[10px] text-sm text-brandBlack "
                  ></Text>
                </Button>
                <Text as="h4" className="font-bold text-sm mb-4">
                  Terms & Conditions
                </Text>
              </Box>
              <Box
                as="div"
                tabIndex={isActive ? 0 : -1}
                className="custom-scroll h-[300px] overflow-y-scroll offersCardHoverContent overflow-auto max-h-[calc(100%-105px)] mb-[0px] bg-grey100 py-[8px] px-[16px]"
              >
                <Text as="p" className="text-sm mb-4">
                  {featuredCardData?.tcsDescription ||
                    "No terms and conditions available."}
                </Text>
              </Box>
              <Box
                as="div"
                className="text-sm relative offersCardHoverFooter flex items-center justify-between bottom-[0px] w-full py-[8px] px-4 2xs:px-[8px] xs:px-[16px]"
              >
                <Text as="p" className="text-sm 2xs:text-xs xs:text-sm">
                  {featuredCardData?.terms_start_head || "Starts"}:{" "}
                  <strong>{formattedStartDate}</strong>
                </Text>
                <Text as="p" className="text-sm 2xs:text-xs xs:text-sm">
                  {featuredCardData?.terms_end_head || "Expires"}:{" "}
                  <strong>{formattedEndDate}</strong>
                </Text>
              </Box>
            </Box>
          </Box>

          {/* Back Side */}
          <Box
            as="div"
            tabIndex={flipped ? 0 : -1}
            className="backdata top-0 absolute pt-[2px] w-full h-full text-brandBlack flex items-start justify-center rotate-y-180 backface-hidden"
            aria-hidden={!flipped}
          >
            <Box
              as="div"
              className="p-4 h-full w-full rounded-[12px] bg-brandWhite shadow-cardShadow "
            >
              <Box
                as="div"
                className="flex items-center justify-between gap-[5px] mb-[25px] border-b-[1px] border-grey250 pb-[20px]"
              >
                <Text as="h4" className="font-bold text-sm mb-0">
                  Offer Information
                </Text>
                <Button
                  tabIndex={flipped ? 0 : -1}
                  onClick={toggleFlip}
                  ref={closeButtonRef}
                  variant="icon"
                  className="!p-0"
                  aria-label="Close offer information"
                >
                  <span className="icon-close w-[16px] text-body text-[#000]"></span>
                </Button>
              </Box>
              <Box
                as="div"
                className="mb-[15px] overflow-auto max-h-[calc(100%-75px)]"
              >
                <Text
                  as="p"
                  className="text-brandBlack text-sm text-center"
                  dangerouslySetInnerHTML={{
                    __html: featuredCardData?.description || "",
                  }}
                ></Text>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {loginModalOpen && (
        <ProductLoginModal
          loginModalOpen={loginModalOpen}
          loginModalClose={handleLoginModal}
          showQuantityText={showQuantityText}
          isHash={isHash}
          productToBeAdded={featuredCardData}
          onQuantityUpdate={() => setQuantity(1)}
          addOffersToStorage={addOffersToStorage}
          openDialog={openDialog}
          offerCart={true}
        />
      )}
    </>
  );
}
